import { BubbleMenu, Editor } from '@tiptap/react'
import React, { FC, useEffect, useRef, useState } from 'react'
import useEditorSelection from '@chegg-wt/core-wa/hooks/useEditorSelection'
import { useSelector, useDispatch } from 'react-redux'
import {
  sentenceOffsetsSelector,
  paperIdSelector,
  enabledWAFeaturesSelector,
  paperTitleSelector,
  feedbacksSelector,
  paperStatusSelector,
  paperListSelector,
} from '@chegg-wt/core-wa/state/selectors'
import { ErrorData, SentenceOffset } from '@chegg-wt/core-wa/state/types'
import synonymsDataHandler from '@chegg-wt/core-wa/utils/pusherDataHandlers/synonymsDataHandler'
import {
  setQuickActionPanelType,
  setRewritePanelSentences,
} from '@chegg-wt/core-wa/state/actions'
import { FeedbackType, QuickActionPanelType } from '@chegg-wt/core-wa/types'
import { colors } from '@chegg-ui/theme'
import {
  emitComponentViewEvent,
  emitNotificationEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { componentOrigins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { REWRITE_ERROR_HEADING } from '@chegg-wt/core-wa/state/constants'
import { getSelection, getWordCount } from '@chegg-wt/core-wa/utils/helpers'
import { QA_WORD_COUNT_LIMIT } from '@chegg-wt/core-wa/constants/paraphraser'
import history from '@chegg-wt/core-wa/utils/history/history'
import {
  HistoryChangeType,
  HistoryInteractionType,
} from '@chegg-wt/core-wa/utils/history/types'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { SynonymIcon } from '../icons/index'
import {
  BubbleOptionsContainer,
  BubbleOptionsButtonLeft,
  BubbleMenuHeader,
  BubbleMenuSuggestionsContainer,
  SuggestionsTitleWithIcon,
  SuggestionsHeader,
  SynonymsContainer,
  SynonymSuggestionBtn,
  ShimmerSynonyms,
} from './styled'
import {
  ExpandIconButton,
  ParaphraseIconButton,
  ShortenIconButton,
  SynonymsIconButton,
} from './IconButtons/IconButtons'
import { SynonymErrorState } from './ErrorStates'
import { SuggestionTitle } from './ErrorStates/styled'
import {
  onSynonymQuickActionClick,
  unBindTheSynonymEventOnPusher,
} from './utils/onSynonymQuickActionClick'
import {
  getSynonymsSentenceOffset,
  shouldDisableRewrite,
  shouldDisableSynonyms,
} from './utils/disableQuickActionsLogic'
import { getCompleteWords, getSelectedSentences } from './utils/getCompleteSelection'
import { fetchSentenceOffsets, nonBoundaryText } from './utils/updateSentenceOffsets'

const isSafari = () => {
  const { userAgent } = window.navigator
  return userAgent.includes('Safari') && !userAgent.includes('Chrome')
}

export const BubbleMenuContainer: FC<{
  editor: Editor
  hasInitialError?: ErrorData
  hasSynonyms?: string[]
}> = ({ editor, hasInitialError = null, hasSynonyms = [] }) => {
  const [synonyms, setSynonyms] = useState<string[]>(hasSynonyms)
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false)
  const dispatch = useDispatch()
  const paperId = useSelector(paperIdSelector)
  const paperTitle = useSelector(paperTitleSelector)
  const feedbacks = useSelector(feedbacksSelector)
  const paperState = useSelector(paperStatusSelector)

  const selectedSentenceOffSet = useRef<SentenceOffset>()
  const [error, setError] = useState<ErrorData>(hasInitialError)
  const selection = useEditorSelection()
  const sentenceOffsets = useSelector(sentenceOffsetsSelector)
  const enabledWAFeaturesSelectorFlag = useSelector(enabledWAFeaturesSelector)
  const isLoggedIn = useSelector(loggedInSelector)
  const paperList = useSelector(paperListSelector)

  const showSynonymsPopOver =
    synonyms.length > 0 || Boolean(error) || isLoadingSuggestions

  useEffect(() => {
    if (showSynonymsPopOver)
      if (synonyms.length) {
        const { selectedText } = selection
        const clubbedSynonyms = synonyms.map((synonym) => synonym.toLowerCase()).join('|')
        dispatch(
          emitComponentViewEvent({
            origin: componentOrigins.proofReaderSynonymMenu,
            text: selectedText,
            value: clubbedSynonyms,
          })
        )
      }
  }, [showSynonymsPopOver, synonyms.length])

  const handleApplySynonym = (text: string) => {
    const { selectedText, from } = selection
    dispatch(
      userClick({
        origin: origins.proofReaderSynonymSuggestionClick,
        text: selectedText,
        value: text,
      })
    )

    if (!selectedSentenceOffSet.current)
      // eslint-disable-next-line no-console
      return console.error('selected sentence not present')

    const impactedSentence = selectedSentenceOffSet.current

    history.addHistoryChangeDetails({
      type: HistoryChangeType.SYNONYM,
      action: HistoryInteractionType.SUGGESTION_ACCEPTED,
    })
    editor
      .chain()
      .replaceCustomUnderlineText(
        {
          id: impactedSentence.id,
          offset: from,
          length: selectedText.length,
          nonManualUpdateType: FeedbackType.SYNONYM,
        },
        text
      )
      .run()

    // ? sentence offsets and feedbacks would be adjusted automatically via manual edit useeditor hook
  }

  const handleSynonymData = (synonymsFromEvent: string[]) => {
    setIsLoadingSuggestions(false)
    setSynonyms(synonymsFromEvent)
  }

  const sendSynonymQuickActionSelectedRioCall = () => {
    const { from, to } = getSelection(editor.view?.state)
    const { originalText, offset } = selectedSentenceOffSet.current
    const oldStart = from - offset
    const oldEnd = to - offset
    const { completeWords } = getCompleteWords(originalText, oldStart, oldEnd)

    dispatch(
      userClick({
        origin: origins.proofReaderSynonymClick,
        text: completeWords,
      })
    )
  }
  const handleSynonymClick = async (retry: boolean = false) => {
    const { sentences, paperId: currentPaperId } = await fetchSentenceOffsets({
      sentenceOffsets,
      selection,
      paperId,
      paperTitle,
      feedbacks,
      paperState,
      dispatch,
      isLoggedIn,
      paperList,
    })
    if (!sentenceOffsets.length || nonBoundaryText(selection, sentenceOffsets)) {
      getSynonymsSentenceOffset(selection, sentences, selectedSentenceOffSet)
    }
    onSynonymQuickActionClick({
      paperId: currentPaperId,
      selectedSentenceOffSet,
      handleSynonymData,
      setIsLoadingSuggestions,
      setError,
    })
    if (!retry) sendSynonymQuickActionSelectedRioCall()
  }

  const handleRewriteClick = async (actionType: QuickActionPanelType) => {
    const { sentences: currentSentenceOffsets } = await fetchSentenceOffsets({
      sentenceOffsets,
      selection,
      paperId,
      paperTitle,
      feedbacks,
      paperState,
      dispatch,
      isLoggedIn,
      paperList,
    })
    const { from, to } = selection
    const selectedSentenceOffsets = getSelectedSentences(currentSentenceOffsets, from, to)
    dispatch(setQuickActionPanelType(actionType))
    dispatch(setRewritePanelSentences(selectedSentenceOffsets))

    if (selectedSentenceOffsets.length === 0)
      return console.error('no matching offsets for rewrite!')
  }

  const { draftQuickActions } = enabledWAFeaturesSelectorFlag

  const isSynonymsDisabled = shouldDisableSynonyms(
    selection,
    sentenceOffsets,
    selectedSentenceOffSet,
    draftQuickActions
  )

  const isRewriteDisabled = shouldDisableRewrite(
    selection,
    sentenceOffsets,
    draftQuickActions
  )
  const isExpandEnabled = enabledWAFeaturesSelectorFlag.expand && !isRewriteDisabled
  const isShortenEnabled = enabledWAFeaturesSelectorFlag.shorten && !isRewriteDisabled

  const hasError = !!error

  useEffect(() => {
    if (hasError) {
      dispatch(
        emitNotificationEvent({
          type: 'error',
          message_name: 'quick actions unavailable',
          message_detail: REWRITE_ERROR_HEADING,
          element: {
            name: 'synonym',
            type: 'component',
          },
        })
      )
    }
  }, [hasError])

  useEffect(() => {
    return () => {
      // Unbind the event on dismounting of popover
      unBindTheSynonymEventOnPusher()
    }
  }, [])

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        duration: 100,
        placement: 'bottom-start',
        onCreate(instance) {
          if (isSafari()) {
            // ? ref - https://github.com/ueberdosis/tiptap/issues/3471
            // Capture the blur event that was added in extension-bubble-menu and stopImmediatePropagation
            instance.popper.firstChild?.addEventListener('blur', (event) => {
              event.preventDefault()
              event.stopImmediatePropagation()
            })
          }
        },
        onHide() {
          setSynonyms([])
          setError(null)
          setIsLoadingSuggestions(false)
          synonymsDataHandler.disableSynonymFeedbackAcceptance()
        },
        onShow() {
          const { selectedText } = getSelection(editor.view?.state)
          const quickActionBarType =
            getWordCount(selectedText) <= QA_WORD_COUNT_LIMIT ? 'synonyms' : 'rewrite'
          dispatch(
            emitComponentViewEvent({
              origin: componentOrigins.proofReaderParaphraseActionBar,
              text: selectedText,
              metadata: {
                proofReaderCardAction: quickActionBarType,
              },
            })
          )
        },
      }}
    >
      <BubbleOptionsContainer suggestionsOpen={showSynonymsPopOver}>
        {!showSynonymsPopOver && (
          <BubbleMenuHeader>
            <BubbleOptionsButtonLeft>
              {!isSynonymsDisabled && (
                <SynonymsIconButton onClick={() => handleSynonymClick()} />
              )}
              {!isRewriteDisabled && (
                <ParaphraseIconButton
                  onClick={() => handleRewriteClick(QuickActionPanelType.PARAPHRASE)}
                />
              )}
              {isShortenEnabled && (
                <ShortenIconButton
                  onClick={() => handleRewriteClick(QuickActionPanelType.SHORTEN)}
                />
              )}
              {isExpandEnabled && (
                <ExpandIconButton
                  onClick={() => handleRewriteClick(QuickActionPanelType.EXPAND)}
                />
              )}
            </BubbleOptionsButtonLeft>
          </BubbleMenuHeader>
        )}
        {showSynonymsPopOver && (
          <BubbleMenuSuggestionsContainer>
            <SuggestionsHeader>
              <SuggestionsTitleWithIcon>
                <SynonymIcon color={hasError ? colors.grey400 : colors.grey900} />
                <SuggestionTitle hasError={hasError}>
                  Synonyms for <strong>{selection?.selectedText}</strong>
                </SuggestionTitle>
              </SuggestionsTitleWithIcon>
            </SuggestionsHeader>
            <SynonymsContainer data-test="wa-synonyms-container">
              {error && (
                <SynonymErrorState handleTryAgain={() => handleSynonymClick(true)} />
              )}

              {synonyms.map((synonym) => (
                <SynonymSuggestionBtn
                  key={synonym}
                  onClick={() => handleApplySynonym(synonym)}
                >
                  {synonym}
                </SynonymSuggestionBtn>
              ))}
              {isLoadingSuggestions && <ShimmerSynonyms line={10} />}
            </SynonymsContainer>
          </BubbleMenuSuggestionsContainer>
        )}
      </BubbleOptionsContainer>
    </BubbleMenu>
  )
}
